import React, {useState} from "react";
import {useAsync} from "react-async-hook";
import {useLocation, useParams} from "react-router-dom";
import {MenuMaterialForm, MenuMaterialFormValues} from "../../components/pages/admin/MenuMaterial/MenuMaterialForm";
import {useSystemMessage} from "../../hooks/useSystemMessage";
import useAppRouter from "../../hooks/useAppRouter";
import {menuMaterialApi} from "../../apis/admin/menu/menuMaterialApi";
import {MenuMaterialType} from "../../enum/menuMaterial/MenuMaterialType";
import {ApiClientError, ResponseCode} from "../../apis/errors";
import {logError} from "../../libs/utils/logError";

type UrlParams = {
  id: string;
}

export const MenuMaterialEditPage: React.FC = () => {
  const { success } = useSystemMessage();
  const [ abortController, setAbortController ] = useState<AbortController | undefined>();
  const { id } = useParams<UrlParams>();
  const menuMaterialId: number = parseInt(id, 10);
  const [ defaultValues, setDefaultValues ] = useState<MenuMaterialFormValues>({
    name: '',
    content: '',
    materialTypeId: MenuMaterialType.URL,
    level: null,
    webUrl: '',
    filename: null,
    attachment: null,
  });
  const [ isLoadedDefaultValues, setIdLoadedDefaultValues ] = useState<boolean>(false);
  const [ apiClientError, setApiClientError ] = useState<ApiClientError | null>(null);
  const { goto } = useAppRouter();
  const location = useLocation();

  useAsync(async () => {
    const fetch = async () => {
      setAbortController(new AbortController());
      try
      {
        const response = await menuMaterialApi.show(menuMaterialId, { abortSignal: abortController?.signal });
        setDefaultValues({
          name: response.menuMaterial.name,
          content: response.menuMaterial.content,
          materialTypeId: response.menuMaterial.materialTypeId,
          level: response.menuMaterial.level,
          webUrl: response.menuMaterial.webUrl,
          filename: response.menuMaterial.filename,
          attachment: null,
        });
        setAbortController(undefined);
        setIdLoadedDefaultValues(true);

      } catch(e) {
        await logError(
          location.pathname,
          'メニュー資料詳細の取得に失敗しました。',
          e
        );

        if ((e instanceof ApiClientError) && (e.code === ResponseCode.NOT_FOUND)) {
          goto('notFoundError');
          return;
        }

        goto('menuMaterialList');
      }
    };
    fetch();
  }, []);

  const clearApiClientError = async (): Promise<void> => {
    setApiClientError(null);
  }

  const onSave = async (data: MenuMaterialFormValues): Promise<void> => {
    setAbortController(new AbortController());
    try {
      await menuMaterialApi.update(
        menuMaterialId,
        {
          name: data.name,
          content: data.content,
          materialTypeId: data.materialTypeId,
          webUrl: data.materialTypeId === MenuMaterialType.URL ? data.webUrl : null,
          level: data.materialTypeId === MenuMaterialType.URL ? null : 1,
          filename: data.materialTypeId !== MenuMaterialType.URL ? data.filename : null,
          attachment: data.materialTypeId !== MenuMaterialType.URL ? data.attachment : null,
        },
        { abortSignal: abortController?.signal });
      setAbortController(undefined);
      await success('メニュー資料を更新しました。');
      goto('menuMaterialList');

    } catch (e) {
      await logError(
        location.pathname,
        'メニュー資料の更新に失敗しました。',
        e
      );

      if (e instanceof ApiClientError) {
        setApiClientError(e);
        return;
      }

      goto('internalServerError');
    }
  }

  return (
    <>
      {isLoadedDefaultValues &&
        (
          <MenuMaterialForm
            {...{
              defaultValues,
              isEdit: true,
              apiClientError,
              clearApiClientError,
              onSave,
            }}
          />
        )
      }
    </>
  );
}
